import { createContext, useContext, useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { continueRender, delayRender, getRemotionEnvironment } from "remotion";
import { useAssetUrl } from "../assets.js";
import {
  bookendTextDefaults,
  findBookendFont,
  nearestWeight,
  primaryFamily,
  type TextStyle,
  type Timeline,
} from "@veyso/core";

/**
 * Any size makes a valid font shorthand; `document.fonts.load` matches on the
 * family and weight and ignores the rest.
 */
const SPEC_SIZE = 100;

/** The part of `document.fonts` this needs, so the logic can be tested off a browser. */
export interface FontSetLike {
  load: (spec: string) => Promise<unknown>;
  ready: Promise<unknown>;
}

const spec = (family: string, weight: number, italic: boolean): string =>
  `${italic ? "italic " : ""}${weight} ${SPEC_SIZE}px "${family}"`;

/**
 * What one line of a card will actually be drawn in.
 *
 * The weight goes through `nearestWeight` for the same reason the renderer
 * does: asking for an 800 the bundle does not carry loads nothing and draws a
 * synthetic bold, and then the frame is released before the real face arrives.
 */
function lineSpec(which: "title" | "subtitle", style: TextStyle | undefined): string {
  const fallback = bookendTextDefaults[which];
  const stack = style?.fontFamily ?? fallback.fontFamily;
  const bold = style?.bold ?? fallback.bold;
  const wanted = bold ? fallback.weights.bold : fallback.weights.normal;
  const entry = findBookendFont(stack);
  return spec(
    primaryFamily(stack),
    entry ? nearestWeight(entry.weights, wanted) : wanted,
    style?.italic ?? fallback.italic,
  );
}

/**
 * Every face a timeline will draw, once each.
 *
 * Only what is on screen: a disabled bookend and a card with no subtitle cost
 * nothing. The two Inter entries at the end are the overlay chrome — the focus
 * callout is drawn in Inter whatever the card is set in, and an export whose
 * callouts came out in Liberation would be just as wrong as one whose title did.
 */
export function timelineFontSpecs(timeline: Timeline): string[] {
  const specs = new Set<string>();

  for (const section of [timeline.sections.head, timeline.sections.end]) {
    if (!section?.enabled || section.content.kind !== "card") continue;
    for (const element of section.content.elements) {
      if (element.kind === "text" && element.text) specs.add(lineSpec(element.role, element.style));
    }
  }

  if (timeline.captions.enabled && timeline.captions.cues.length > 0) {
    const style = timeline.captions.style;
    specs.add(spec(primaryFamily(style.fontFamily), style.fontWeight, false));
  }

  specs.add(spec("Inter", 600, false));
  return [...specs];
}

/**
 * Waits for a set of faces, and always resolves.
 *
 * Two things must not happen. A frame must not be drawn before its font
 * arrives, which is the bug this exists to prevent — the editor looks right,
 * the export comes out in the fallback, and nothing announces it. And a font
 * that never arrives must not hold the render open until Remotion times the
 * frame out and fails it: a card in the wrong face is a bad export, and no
 * export at all is a worse one. Hence the deadline, and the swallowed errors.
 */
export async function loadFontSpecs(
  fonts: FontSetLike | undefined,
  specs: readonly string[],
  timeoutMs = 8000,
): Promise<void> {
  if (!fonts || specs.length === 0) return;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });

  const loaded = Promise.all(specs.map((one) => Promise.resolve(fonts.load(one)).catch(() => undefined)))
    .then(() => fonts.ready)
    .then(() => undefined)
    .catch(() => undefined);

  try {
    await Promise.race([loaded, deadline]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Holds the frame until the timeline's fonts have loaded.
 *
 * Only while rendering. In the editor the Player would stall on every keystroke
 * that changed a font, and a preview that flickers to buy correctness it does
 * not need is a bad trade — the browser swaps the face in a moment later and
 * nobody is encoding the result.
 */
/**
 * `@font-face` rules for the fonts this project carries.
 *
 * The bundled library needs none of this — its stylesheets are imported by the
 * entry point and emitted beside the code. An uploaded face is an asset, so its
 * rule is written here and pointed at whichever asset URL this surface uses,
 * which is what lets the editor and the encoder resolve the same family to the
 * same file without either of them reaching the network.
 */
export const ProjectFontFaces: FC<{ fonts: Timeline["fonts"] }> = ({ fonts }) => {
  const assetUrl = useAssetUrl();
  if (fonts.length === 0) return null;

  const css = fonts
    .map((font) => {
      const url = assetUrl(font.assetKey);
      if (!url) return "";
      // The family name is quoted and the quotes are stripped from it first:
      // a stray quote would close the declaration and take the rest with it.
      const family = font.family.replace(/["\\]/g, "");
      return `@font-face{font-family:"${family}";src:url("${url}");font-display:block;}`;
    })
    .join("");

  return <style>{css}</style>;
};

/**
 * Whether this tree is being encoded rather than previewed.
 *
 * Remotion answers this with `getRemotionEnvironment().isRendering`, and that
 * is what `FontGate` used to ask. Under `@remotion/web-renderer` it comes back
 * **false** — the composition is rendered outside the environment context that
 * renderer sets up, so `isRendering` and `isClientSideRendering` are both the
 * default.
 *
 * The consequence was invisible and intermittent. The gate never called
 * `delayRender`, so nothing forced the card's faces to download, and whether a
 * frame got the right typeface came down to whether the browser had happened
 * to finish fetching it. Two renders of the same composition measured 370 and
 * 413 pixels of ink for the same words.
 *
 * So the caller says so instead. It is the one thing that always knows, it is
 * the same shape as `MediaComponentsProvider` — which exists because the same
 * renderer needs different video components — and it cannot be wrong about a
 * question it is the answer to.
 */
const RenderingContext = createContext(false);

export const RenderingProvider: FC<{ children: ReactNode }> = ({ children }) => (
  <RenderingContext.Provider value={true}>{children}</RenderingContext.Provider>
);

/**
 * True while encoding, by either route.
 *
 * Remotion's own answer is still asked first, because it is right on the
 * worker and needs no cooperation from the caller. The context is what covers
 * the browser, where it is not.
 */
export function useIsRendering(): boolean {
  const told = useContext(RenderingContext);
  return getRemotionEnvironment().isRendering || told;
}

export const FontGate: FC<{ timeline: Timeline }> = ({ timeline }) => {
  const rendering = useIsRendering();
  const specs = timelineFontSpecs(timeline);
  const key = specs.join("|");
  const [handle] = useState(() => (rendering ? delayRender("Loading the card fonts") : null));
  // A handle may only be continued once, and a timeline whose fonts changed
  // mid-render would otherwise try twice.
  const released = useRef(false);

  useEffect(() => {
    if (handle === null || released.current) return;
    const fonts = (globalThis as { document?: { fonts?: FontSetLike } }).document?.fonts;
    void loadFontSpecs(fonts, key.split("|")).then(() => {
      if (released.current) return;
      released.current = true;
      continueRender(handle);
    });
    // `key` rather than `specs`: a fresh array every render would loop forever.
  }, [handle, key]);

  return <ProjectFontFaces fonts={timeline.fonts} />;
};
